import type { Grade } from "../screens/cardName.js";

export type SfxName =
  | "drumRoll"
  | "revealCommon"
  | "revealUncommon"
  | "revealRare"
  | "revealEpic"
  | "revealLegendary"
  | "revealRelic";

export type SfxClip = (ctx: AudioContext, target: AudioNode, now: number) => void;

interface ToneOptions {
  type: OscillatorType;
  startFreq: number;
  endFreq?: number;
  startTime: number;
  duration: number;
  peakGain: number;
  attack?: number;
  release?: number;
}

function tone(ctx: AudioContext, target: AudioNode, options: ToneOptions): void {
  const {
    type,
    startFreq,
    endFreq = startFreq,
    startTime,
    duration,
    peakGain,
    attack = 0.005,
    release = duration,
  } = options;
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(startFreq, startTime);
  if (endFreq !== startFreq) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(endFreq, 1), startTime + duration);
  }
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(peakGain, startTime + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + attack + release);
  osc.connect(gain);
  gain.connect(target);
  osc.start(startTime);
  osc.stop(startTime + attack + release + 0.02);
}

// Anticipation: 7 quick ticks accelerating in pitch and tempo. Lines up
// with the ~480 ms shake phase of runRoll so the audio resolves just
// before the flip-reveal lands the new skin.
const drumRoll: SfxClip = (ctx, target, now) => {
  const ticks = 7;
  const totalMs = 480;
  for (let i = 0; i < ticks; i += 1) {
    const t = i / (ticks - 1);
    const offsetMs = totalMs * (t * t); // accelerating
    const freq = 220 + t * 560;
    tone(ctx, target, {
      type: "triangle",
      startFreq: freq,
      startTime: now + offsetMs / 1000,
      duration: 0.04,
      peakGain: 0.07 + t * 0.05,
      attack: 0.001,
      release: 0.05,
    });
  }
};

// Reveal stingers, scaled by grade. The lowest grades land flat and
// quick; higher grades stack harmonics, lengthen the tail, and brighten
// the body so a RELIC roll reads as a discrete event.
const revealCommon: SfxClip = (ctx, target, now) => {
  tone(ctx, target, {
    type: "triangle",
    startFreq: 320,
    endFreq: 220,
    startTime: now,
    duration: 0.18,
    peakGain: 0.1,
    attack: 0.004,
    release: 0.2,
  });
};

const revealUncommon: SfxClip = (ctx, target, now) => {
  tone(ctx, target, {
    type: "triangle",
    startFreq: 392,
    startTime: now,
    duration: 0.22,
    peakGain: 0.11,
    attack: 0.003,
    release: 0.24,
  });
  tone(ctx, target, {
    type: "sine",
    startFreq: 196,
    startTime: now,
    duration: 0.22,
    peakGain: 0.08,
    attack: 0.004,
    release: 0.24,
  });
};

const revealRare: SfxClip = (ctx, target, now) => {
  // Major chord (C-E-G) — classic "found something good" feel.
  tone(ctx, target, {
    type: "triangle",
    startFreq: 523,
    startTime: now,
    duration: 0.32,
    peakGain: 0.12,
    attack: 0.003,
    release: 0.34,
  });
  tone(ctx, target, {
    type: "triangle",
    startFreq: 659,
    startTime: now + 0.04,
    duration: 0.3,
    peakGain: 0.11,
    attack: 0.003,
    release: 0.32,
  });
  tone(ctx, target, {
    type: "triangle",
    startFreq: 784,
    startTime: now + 0.08,
    duration: 0.3,
    peakGain: 0.11,
    attack: 0.003,
    release: 0.32,
  });
};

const revealEpic: SfxClip = (ctx, target, now) => {
  // Stair-step climb plus low body. Brighter peak than RARE.
  tone(ctx, target, {
    type: "sine",
    startFreq: 130,
    startTime: now,
    duration: 0.5,
    peakGain: 0.16,
    attack: 0.005,
    release: 0.55,
  });
  for (let i = 0; i < 3; i += 1) {
    tone(ctx, target, {
      type: "triangle",
      startFreq: 523 + i * 130,
      startTime: now + i * 0.06,
      duration: 0.45 - i * 0.05,
      peakGain: 0.12,
      attack: 0.002,
      release: 0.5,
    });
  }
};

const revealLegendary: SfxClip = (ctx, target, now) => {
  // Fanfare: dominant-tonic with an octave shimmer on top.
  tone(ctx, target, {
    type: "triangle",
    startFreq: 392,
    startTime: now,
    duration: 0.7,
    peakGain: 0.14,
    attack: 0.004,
    release: 0.75,
  });
  tone(ctx, target, {
    type: "triangle",
    startFreq: 523,
    startTime: now + 0.08,
    duration: 0.62,
    peakGain: 0.13,
    attack: 0.004,
    release: 0.7,
  });
  tone(ctx, target, {
    type: "triangle",
    startFreq: 784,
    startTime: now + 0.16,
    duration: 0.6,
    peakGain: 0.12,
    attack: 0.003,
    release: 0.7,
  });
  tone(ctx, target, {
    type: "sine",
    startFreq: 1568,
    startTime: now + 0.18,
    duration: 0.5,
    peakGain: 0.06,
    attack: 0.004,
    release: 0.55,
  });
};

const revealRelic: SfxClip = (ctx, target, now) => {
  // Wide chord, two octaves, with a slow shimmer. Long tail so the
  // moment lingers.
  const root = 523;
  const partials = [1, 1.25, 1.5, 2, 2.5, 3];
  for (let i = 0; i < partials.length; i += 1) {
    const freq = root * (partials[i] ?? 1);
    tone(ctx, target, {
      type: i < 4 ? "triangle" : "sine",
      startFreq: freq,
      startTime: now + i * 0.04,
      duration: 0.95 - i * 0.05,
      peakGain: 0.1 - i * 0.008,
      attack: 0.004,
      release: 1.05,
    });
  }
  // Sub bass for body
  tone(ctx, target, {
    type: "sine",
    startFreq: root / 4,
    startTime: now,
    duration: 1.1,
    peakGain: 0.13,
    attack: 0.008,
    release: 1.15,
  });
};

export const sfxClips: Record<SfxName, SfxClip> = {
  drumRoll,
  revealCommon,
  revealUncommon,
  revealRare,
  revealEpic,
  revealLegendary,
  revealRelic,
};

export function revealClipFor(grade: Grade): SfxName {
  switch (grade) {
    case "COMMON":
      return "revealCommon";
    case "UNCOMMON":
      return "revealUncommon";
    case "RARE":
      return "revealRare";
    case "EPIC":
      return "revealEpic";
    case "LEGENDARY":
      return "revealLegendary";
    case "RELIC":
      return "revealRelic";
  }
}
